import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Loader2, ExternalLink, AlertCircle, AlertTriangle, FileText, Send, X, Eye, UserPlus, Check, Plus } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, useToast } from '@hiring-intelligence/ui';
import { candidateAiApi, sourcingApi } from '@/services/api';
import { CandidateDetailModal } from './CandidateDetailModal';

const CONFIDENCE_BADGE: Record<string, string> = {
	high: 'bg-emerald-100 text-emerald-700',
	medium: 'bg-amber-100 text-amber-700',
	low: 'bg-slate-100 text-slate-600'
};

const dateFmt = (d: string) =>
	new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Quick-fill helpers shown above the composer — clicking fills the input for editing. */
const INSTRUCTION_SUGGESTIONS = [
	'Focus Tier 1 on direct competitors in the role location',
	'Only candidates currently based in the role location',
	'Only candidates open to work',
	'Focus on startup / scale-up backgrounds',
	'Include regional candidates open to relocation'
];

/**
 * "Candidate Discovery" — full-width section above Search Candidates in the Sourcing
 * tab. Chat-style layout: each generation is one turn of a continuing conversation
 * (thread = versions sharing a chatId; follow-up turns refine the SignalHire searches
 * and exclude people already shown). "New Chat" starts a fresh thread; every thread
 * stays browsable as history. The candidate list of any turn opens in the preview pane.
 */
export function CandidateDiscoveryCard({ hiringRequestId }: { hiringRequestId: string }) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	// null = follow the newest chat; 'new' = composing a fresh chat.
	const [chatKey, setChatKey] = useState<string | null>(null);
	// Which turn's candidate list shows in the preview pane (null = latest completed in the thread).
	const [previewVersionId, setPreviewVersionId] = useState<string | null>(null);
	const [instructions, setInstructions] = useState('');
	const [previewOpen, setPreviewOpen] = useState(true);
	// Discovery candidate opened in the detail popup (SignalHire-backed cards only).
	const [detailCandidate, setDetailCandidate] = useState<any | null>(null);
	const threadEndRef = useRef<HTMLDivElement | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: ['candidate-discovery', hiringRequestId],
		queryFn: () => candidateAiApi.listSuggestions(hiringRequestId),
		refetchOnWindowFocus: false,
		// Poll while a generation is running in the worker.
		refetchInterval: (query) => {
			const versions = query.state.data?.data || [];
			return versions.some((v: any) => v.status === 'processing') ? 5000 : false;
		}
	});

	const versions: any[] = data?.data || []; // version DESC from the API
	const isProcessing = versions.some((v) => v.status === 'processing');

	// Group versions into chats (legacy rows fall back to their own id as chat key).
	const chatKeyOf = (v: any) => v.chatId || v.id;
	const chats: { key: string; versions: any[] }[] = [];
	{
		const byKey = new Map<string, any[]>();
		for (const v of versions) {
			const k = chatKeyOf(v);
			if (!byKey.has(k)) {
				byKey.set(k, []);
				chats.push({ key: k, versions: byKey.get(k)! });
			}
			byKey.get(k)!.push(v);
		}
		// Within a chat render oldest → newest.
		for (const c of chats) c.versions.reverse();
	}

	const isNewChat = chatKey === 'new';
	const activeChat = isNewChat ? null : (chats.find((c) => c.key === chatKey) ?? chats[0] ?? null);
	const thread = activeChat?.versions || [];

	// Preview target: an explicitly opened turn, else the thread's latest completed one.
	const previewVersion =
		(previewVersionId && versions.find((v) => v.id === previewVersionId)) ||
		[...thread].reverse().find((v) => v.status === 'completed' && v.content?.candidates?.length) ||
		null;
	const content = previewVersion?.content ?? null;
	const candidates: any[] = content?.candidates || [];
	// SignalHire-backed versions carry tier metadata → grouped sections; older
	// AI-web-search versions don't → legacy flat list.
	const tierGroups: { meta: any; items: any[] }[] | null = content?.tiers?.length
		? content.tiers.map((t: any) => ({ meta: t, items: candidates.filter((c: any) => c.tier === t.tier) }))
		: null;
	const showPreview = previewOpen && candidates.length > 0;

	// Saved pipeline candidates (shared cache with the Candidates tab) — used to mark
	// discovered people that are already in the pipeline.
	const { data: savedData } = useQuery({
		queryKey: ['sourced-candidates', hiringRequestId],
		queryFn: () => sourcingApi.getCandidates(hiringRequestId),
		refetchOnWindowFocus: false
	});
	// Compare LinkedIn URLs ignoring protocol, www, query string, case and trailing slash
	// so an already-looked-up person never shows the Look Up button again.
	const normalizeUrl = (u: string) =>
		u.trim().toLowerCase().split('?')[0].replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
	const savedCandidates = (savedData?.data as any[]) || [];
	const savedLinkedinUrls = new Set(
		savedCandidates.map((c) => (c.linkedinUrl ? normalizeUrl(c.linkedinUrl) : null)).filter(Boolean) as string[]
	);
	const savedSignalhireIds = new Set(
		savedCandidates.map((c) => c.signalhireId).filter(Boolean) as string[]
	);

	// Legacy path for old AI-web-search versions whose candidates only carry a LinkedIn URL.
	const lookupMutation = useMutation({
		mutationFn: (linkedinUrl: string) => sourcingApi.addByLinkedIn(hiringRequestId, linkedinUrl),
		onSuccess: (res: any) => {
			toast({
				title: res.message ? 'Already in Pipeline' : 'Candidate Added',
				description: res.message || 'Candidate added. Profile lookup has started and will update automatically.'
			});
			queryClient.invalidateQueries({ queryKey: ['sourced-candidates', hiringRequestId] });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	// SignalHire-discovered candidates carry a signalhireId + profile snapshot — added
	// directly with their real name (no 'Pending lookup...' phase).
	const addMutation = useMutation({
		mutationFn: (c: any) =>
			sourcingApi.addFromDiscovery(hiringRequestId, c.signalhireId, {
				fullName: c.name,
				location: c.location ?? null,
				currentTitle: c.currentTitle ?? null,
				currentCompany: c.currentCompany ?? null,
				skills: c.skills || [],
				experience: c.experience || [],
				openToWork: c.openToWork ?? false
			}),
		onSuccess: (res: any) => {
			toast({
				title: res.message ? 'Already in Pipeline' : 'Candidate Added',
				description: res.message || 'Candidate added to the pipeline. Profile enrichment runs in the background.'
			});
			queryClient.invalidateQueries({ queryKey: ['sourced-candidates', hiringRequestId] });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const generateMutation = useMutation({
		mutationFn: () =>
			candidateAiApi.generateSuggestions(
				hiringRequestId,
				instructions.trim() || undefined,
				isNewChat ? undefined : activeChat?.key
			),
		onSuccess: (res: any) => {
			// A fresh chat gets its server-assigned chatId — follow it.
			if (res?.data?.chatId) setChatKey(res.data.chatId);
			setPreviewVersionId(null);
			setInstructions('');
			queryClient.invalidateQueries({ queryKey: ['candidate-discovery', hiringRequestId] });
			toast({ title: 'Generation started', description: 'Building tier searches and querying SignalHire — usually under a minute.' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const busy = isProcessing || generateMutation.isPending;

	const submit = () => {
		if (busy) return;
		generateMutation.mutate();
	};

	const startNewChat = () => {
		setChatKey('new');
		setPreviewVersionId(null);
		setInstructions('');
	};

	// Keep the thread scrolled to the newest turn.
	useEffect(() => {
		threadEndRef.current?.scrollIntoView({ block: 'nearest' });
	}, [thread.length, isProcessing, activeChat?.key]);

	const chatLabel = (c: { versions: any[] }) => {
		const first = c.versions[0];
		const text = first?.additionalContext?.trim() || 'Find candidates';
		return `${text.length > 34 ? `${text.slice(0, 34)}…` : text} · ${dateFmt(first.createdAt)}`;
	};

	const renderCandidate = (c: any, i: number) => (
		<div
			key={c.signalhireId || `${i}-${c.name}`}
			className={`space-y-2 rounded-lg border p-3 ${c.signalhireId ? 'cursor-pointer transition-colors hover:bg-muted/50' : ''}`}
			onClick={c.signalhireId ? () => setDetailCandidate(c) : undefined}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<p className="truncate text-sm font-medium">{i + 1}. {c.name}</p>
					<p className="truncate text-xs text-muted-foreground">
						{[c.currentTitle, c.currentCompany].filter(Boolean).join(' @ ') || '—'}
						{c.location ? ` · ${c.location}` : ''}
					</p>
				</div>
				<span className="flex shrink-0 items-center gap-1">
					{c.openToWork && (
						<span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Open to work</span>
					)}
					{c.tier && !tierGroups && (
						<span className="rounded-full border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{c.tier}</span>
					)}
					<span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${CONFIDENCE_BADGE[c.confidence] || CONFIDENCE_BADGE.low}`}>
						{c.confidence}
					</span>
				</span>
			</div>
			{c.background && <p className="text-[11px] text-muted-foreground">{c.background}</p>}
			<p className="text-xs">{c.whyFit}</p>
			{Array.isArray(c.signals) && c.signals.length > 0 && (
				<div className="flex flex-wrap gap-1">
					{c.signals.slice(0, 5).map((s: string, j: number) => (
						<span key={j} className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">{s}</span>
					))}
				</div>
			)}
			{Array.isArray(c.skills) && c.skills.length > 0 && (
				<div className="flex flex-wrap gap-1">
					{c.skills.slice(0, 6).map((s: string, j: number) => (
						<span key={j} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{s}</span>
					))}
				</div>
			)}
			<div className="flex flex-wrap items-center gap-3">
				{c.linkedinUrl && (
					<a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
						LinkedIn <ExternalLink className="h-3 w-3" />
					</a>
				)}
				{c.profileUrl && (
					<a href={c.profileUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
						Source <ExternalLink className="h-3 w-3" />
					</a>
				)}
				{c.sourcingPillar && <span className="text-[10px] text-muted-foreground">{c.sourcingPillar}</span>}
				{c.signalhireId ? (
					savedSignalhireIds.has(c.signalhireId) ? (
						<span className="ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-600">
							<Check className="h-3 w-3" /> In pipeline
						</span>
					) : (
						<Button
							size="sm"
							variant="outline"
							className="ml-auto h-6 px-2 text-[11px]"
							disabled={addMutation.isPending}
							onClick={(e) => {
								e.stopPropagation();
								addMutation.mutate(c);
							}}
						>
							{addMutation.isPending && addMutation.variables?.signalhireId === c.signalhireId
								? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
								: <UserPlus className="mr-1 h-3 w-3" />}
							Add to Pipeline
						</Button>
					)
				) : c.linkedinUrl && (
					savedLinkedinUrls.has(normalizeUrl(c.linkedinUrl)) ? (
						<span className="ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-600">
							<Check className="h-3 w-3" /> In pipeline
						</span>
					) : (
						<Button
							size="sm"
							variant="outline"
							className="ml-auto h-6 px-2 text-[11px]"
							disabled={lookupMutation.isPending}
							onClick={(e) => {
								e.stopPropagation();
								lookupMutation.mutate(c.linkedinUrl);
							}}
						>
							{lookupMutation.isPending && lookupMutation.variables === c.linkedinUrl
								? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
								: <UserPlus className="mr-1 h-3 w-3" />}
							Look Up
						</Button>
					)
				)}
			</div>
		</div>
	);

	/** One completed turn's answer: summary bubble + candidate-list asset card. */
	const renderTurnResult = (v: any) => {
		const vContent = v.content;
		const vStats = vContent?.searchStats;
		const vCount = vContent?.candidates?.length ?? 0;
		const isShownInPreview = previewVersion?.id === v.id && showPreview;
		return (
			<div className="mr-auto max-w-[90%] space-y-2">
				<div className="space-y-2 rounded-lg rounded-bl-sm bg-muted px-3 py-2">
					{vContent?.summary && <p className="text-sm">{vContent.summary}</p>}
					{vContent?.searchStrategy && (
						<p className="border-l-2 border-primary/30 pl-2 text-xs text-muted-foreground">{vContent.searchStrategy}</p>
					)}
					{vStats && (
						<p className="text-[11px] text-muted-foreground">
							{vStats.queriesUsed} SignalHire search{vStats.queriesUsed === 1 ? '' : 'es'} · {vStats.profilesReturned} profiles
							{vStats.mockMode && (
								<span className="ml-1.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">mock data</span>
							)}
						</p>
					)}
					{vStats?.quotaExhausted && (
						<p className="flex items-start gap-1.5 text-[11px] text-amber-700">
							<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
							SignalHire daily search quota was exhausted mid-run — some tiers may be incomplete. Try again tomorrow.
						</p>
					)}
				</div>
				{vCount > 0 && (
					<button
						type="button"
						onClick={() => {
							setPreviewVersionId(v.id);
							setPreviewOpen(true);
						}}
						className={`flex w-full items-center gap-3 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-accent ${isShownInPreview ? 'border-primary/60' : ''}`}
					>
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10">
							<FileText className="h-5 w-5 text-primary" />
						</div>
						<div className="min-w-0 flex-1">
							<p className="truncate text-sm font-medium">Candidate List</p>
							<p className="truncate text-xs text-muted-foreground">
								{vCount} candidate{vCount === 1 ? '' : 's'} · v{v.version} · Open preview
							</p>
						</div>
						<Eye className="h-4 w-4 shrink-0 text-muted-foreground" />
					</button>
				)}
			</div>
		);
	};

	return (
		<Card>
			<CardHeader>
				<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
					<div>
						<CardTitle className="flex items-center gap-2">
							<Sparkles className="h-5 w-5 text-primary" />
							Candidate Discovery
						</CardTitle>
						<CardDescription className="mt-1">
							Searches SignalHire for real candidates across three target-company tiers, based on the same source information as the sourcing strategy.
						</CardDescription>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						{chats.length > 0 && (
							<select
								className="h-9 max-w-[260px] rounded-md border border-input bg-transparent px-2 text-xs"
								value={isNewChat ? 'new' : (activeChat?.key || '')}
								onChange={(e) => {
									setChatKey(e.target.value);
									setPreviewVersionId(null);
									setPreviewOpen(true);
								}}
							>
								{isNewChat && <option value="new">New chat…</option>}
								{chats.map((c) => (
									<option key={c.key} value={c.key}>{chatLabel(c)}</option>
								))}
							</select>
						)}
						{chats.length > 0 && !isNewChat && (
							<Button variant="outline" size="sm" className="h-9" onClick={startNewChat} disabled={busy}>
								<Plus className="mr-1 h-3.5 w-3.5" /> New Chat
							</Button>
						)}
					</div>
				</div>
			</CardHeader>
			<CardContent>
				<div className={showPreview ? 'grid gap-4 lg:grid-cols-5' : ''}>
					{/* Chat pane */}
					<div className={`flex h-[70vh] flex-col rounded-lg border ${showPreview ? 'lg:col-span-2' : ''}`}>
						<div className="flex-1 space-y-3 overflow-y-auto p-3">
							{isLoading ? (
								<div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
									<Loader2 className="h-4 w-4 animate-spin" /> Loading…
								</div>
							) : thread.length === 0 ? (
								<>
									<div className="mr-auto max-w-[90%] rounded-lg rounded-bl-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
										{isNewChat
											? 'New chat. Describe a focus below (optional) and send — earlier chats stay available in the history dropdown.'
											: 'No discovery runs yet. Send “Find candidates for this role.” below to search SignalHire for matching people in each target-company tier.'}
									</div>
									<button
										type="button"
										onClick={submit}
										disabled={busy}
										className="ml-auto flex max-w-[85%] items-center gap-2 rounded-lg rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
									>
										{busy && <Loader2 className="h-4 w-4 animate-spin" />}
										Find candidates for this role.
									</button>
								</>
							) : (
								thread.map((v) => (
									<div key={v.id} className="space-y-3">
										<div className="ml-auto max-w-[85%] rounded-lg rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
											{v.additionalContext?.trim() || 'Find candidates for this role.'}
										</div>
										{v.status === 'processing' ? (
											<div className="mr-auto flex max-w-[90%] items-center gap-2 rounded-lg rounded-bl-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
												<Loader2 className="h-4 w-4 animate-spin text-primary" />
												Building tier searches and querying SignalHire… this usually takes under a minute.
											</div>
										) : v.status === 'failed' ? (
											<div className="mr-auto flex max-w-[90%] items-start gap-2 rounded-lg rounded-bl-sm bg-destructive/10 px-3 py-2 text-sm text-destructive">
												<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
												<span>Generation failed: {v.error || 'Unknown error'}. Try sending again.</span>
											</div>
										) : (
											renderTurnResult(v)
										)}
									</div>
								))
							)}
							<div ref={threadEndRef} />
						</div>
						{/* Composer — continues the active chat; a fresh chat starts one. */}
						<div className="border-t">
							{!busy && !instructions && (
								<div className="flex flex-wrap gap-1.5 px-2 pt-2">
									{INSTRUCTION_SUGGESTIONS.map((s) => (
										<button
											key={s}
											type="button"
											onClick={() => setInstructions(s)}
											className="rounded-full border bg-background px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
										>
											{s}
										</button>
									))}
								</div>
							)}
							<form
								className="flex items-center gap-2 p-2"
								onSubmit={(e) => {
									e.preventDefault();
									submit();
								}}
							>
								<Input
									placeholder={thread.length === 0 ? 'Optional focus for this search…' : 'Reply to refine this search…'}
									value={instructions}
									onChange={(e) => setInstructions(e.target.value)}
									disabled={busy}
									className="h-9 text-xs"
								/>
								<Button type="submit" size="sm" className="shrink-0" disabled={busy}>
									{busy
										? <Loader2 className="h-4 w-4 animate-spin" />
										: <Send className="h-4 w-4" />}
								</Button>
							</form>
						</div>
					</div>

					{/* Asset preview pane */}
					{showPreview && previewVersion && (
						<div className="flex h-[70vh] flex-col rounded-lg border lg:col-span-3">
							<div className="flex items-center gap-2 border-b px-3 py-2">
								<FileText className="h-4 w-4 shrink-0 text-primary" />
								<p className="text-sm font-medium">Candidate List</p>
								<p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
									{candidates.length} candidate{candidates.length === 1 ? '' : 's'} · v{previewVersion.version} · {dateFmt(previewVersion.createdAt)}
								</p>
								<button
									type="button"
									onClick={() => setPreviewOpen(false)}
									className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
									aria-label="Close preview"
								>
									<X className="h-4 w-4" />
								</button>
							</div>
							<div className="flex-1 space-y-3 overflow-y-auto p-3">
								{tierGroups ? (
									tierGroups.map(({ meta, items }) => (
										<div key={meta.tier} className="space-y-2">
											<div className="rounded-md bg-muted/60 px-3 py-2">
												<p className="flex items-center gap-2 text-xs font-semibold">
													{meta.label || meta.tier}
													<span className="rounded-full border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
														{items.length} candidate{items.length === 1 ? '' : 's'}
													</span>
												</p>
												{meta.rationale && <p className="mt-0.5 text-[11px] text-muted-foreground">{meta.rationale}</p>}
												{meta.queryDescription && (
													<p className="mt-0.5 truncate text-[10px] text-muted-foreground/70" title={meta.queryDescription}>
														{meta.queryDescription}
													</p>
												)}
											</div>
											{items.length > 0 ? (
												items.map((c, i) => renderCandidate(c, i))
											) : (
												<p className="px-3 py-1 text-[11px] text-muted-foreground">No matching profiles for this tier.</p>
											)}
										</div>
									))
								) : (
									candidates.map((c: any, i: number) => renderCandidate(c, i))
								)}
							</div>
						</div>
					)}
				</div>
			</CardContent>

			{/* Detail popup — full vault profile (skills, experience, Get Info / Get Contacts)
			    with the discovery fit rationale and a save action, like the search results. */}
			<CandidateDetailModal
				candidate={detailCandidate ? {
					signalhireId: detailCandidate.signalhireId,
					name: detailCandidate.name,
					currentTitle: detailCandidate.currentTitle ?? undefined,
					currentCompany: detailCandidate.currentCompany ?? undefined,
					location: detailCandidate.location ?? undefined
				} : null}
				open={!!detailCandidate}
				onOpenChange={(open) => { if (!open) setDetailCandidate(null); }}
				actions={detailCandidate && (
					savedSignalhireIds.has(detailCandidate.signalhireId) ? (
						<span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
							<Check className="h-4 w-4" /> In pipeline
						</span>
					) : (
						<Button
							disabled={addMutation.isPending}
							onClick={() => addMutation.mutate(detailCandidate)}
						>
							{addMutation.isPending && addMutation.variables?.signalhireId === detailCandidate.signalhireId
								? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
								: <UserPlus className="mr-2 h-4 w-4" />}
							Add to Pipeline
						</Button>
					)
				)}
			>
				{detailCandidate && (
					<div className="space-y-2 rounded-md bg-muted/50 p-3">
						<div className="flex flex-wrap items-center gap-1.5">
							{detailCandidate.tier && (
								<span className="rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{detailCandidate.tier}</span>
							)}
							<span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${CONFIDENCE_BADGE[detailCandidate.confidence] || CONFIDENCE_BADGE.low}`}>
								{detailCandidate.confidence} confidence
							</span>
							{detailCandidate.openToWork && (
								<span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Open to work</span>
							)}
						</div>
						{detailCandidate.whyFit && <p className="text-sm">{detailCandidate.whyFit}</p>}
						{Array.isArray(detailCandidate.signals) && detailCandidate.signals.length > 0 && (
							<div className="flex flex-wrap gap-1">
								{detailCandidate.signals.slice(0, 5).map((s: string, j: number) => (
									<span key={j} className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">{s}</span>
								))}
							</div>
						)}
					</div>
				)}
			</CandidateDetailModal>
		</Card>
	);
}
